/**
 * KAN-874: мобильная доска с "Требуют внимания": вместо вкладок и колонок этапов
 * один общий список по всем этапам (useCrmAttentionList). Карточка как на доске
 * (причина и задача внимания) с подписью этапа; клик открывает панель карточки.
 * KAN-880: фильтры доски (поиск, менеджер, без движения) действуют и здесь.
 * KAN-926: карточки по разделам (attention_section), порядок attention_rank внутри раздела.
 * KAN-882: вид доски (managers / funnel) - общий кеш с доской.
 */
import type { CrmRange } from '@/lib/crmTable';
import { Loader2 } from 'lucide-react';
import { useCrmAttentionList } from '@/hooks/useCrmAttentionList';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { CrmBoardView, CrmLeadFilterParams, CrmPipelineCard } from '@/types/crmPipeline';
import { KanbanCard } from './KanbanCard';
import { AttentionSections } from '@/components/attention/AttentionSections';

interface AttentionListProps {
  board: CrmBoardView;
  activity: CrmRange | null;
  filters?: CrmLeadFilterParams;
  clientHref: (card: CrmPipelineCard) => string;
  companyHref?: (companyId: string) => string;
  onOpenCard?: (card: CrmPipelineCard) => void;
}

export function AttentionList({
  board,
  activity,
  filters,
  clientHref,
  companyHref,
  onOpenCard,
}: AttentionListProps): JSX.Element {
  const { items, isLoading, error } = useCrmAttentionList(board, activity, filters);

  if (isLoading) {
    return (
      <div className="flex justify-center py-10" data-testid="crm-attention-list-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border bg-white p-6 text-sm text-red-600" data-testid="crm-attention-list-error">
        Не удалось загрузить список: {extractErrorMessage(error, 'ошибка сервера')}
      </div>
    );
  }
  if (items.length === 0) {
    return <p className="py-10 text-center text-sm text-gray-500">Сейчас ничего не требует внимания</p>;
  }
  return (
    <div data-testid="crm-attention-list" className="min-w-0">
      <AttentionSections
        items={items}
        sectionOf={({ card }) => card.attention_section}
        renderItems={(group) =>
          group.map(({ card, stageTitle }) => (
            <KanbanCard
              key={card.id}
              card={card}
              href={clientHref(card)}
              companyHref={card.company?.id && companyHref ? companyHref(card.company.id) : null}
              onOpen={onOpenCard}
              stageLabel={stageTitle}
            />
          ))
        }
      />
    </div>
  );
}
